/**
 * ProgressPanel.tsx
 * Redesigned progress sidebar with cluster support and visual connections.
 */

import { CheckCircle, Clock, MapPin, Flag } from 'lucide-react';
import { Card } from '@vsat/shared/components';
import { Progress } from '@vsat/shared/components';
import QuestionNavButton from './QuestionNavButton';
import { cn } from '@vsat/shared/lib/utils';
import type { DemoQuestion, DemoAnswerMap } from '../types';
import { useState, useEffect, useRef, useMemo } from 'react';

interface Props {
  questions: DemoQuestion[];
  answers: DemoAnswerMap;
  currentIndex: number;
  timeRemaining: string;
  onQuestionSelect: (index: number) => void;
}

// Available cluster colors (unique colors, no duplicates)
// Each cluster will get a unique color from this palette
const CLUSTER_COLORS = [
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#10B981', // Green
  '#F59E0B', // Orange
  '#EF4444', // Red
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F97316', // Orange-600
  '#A855F7', // Purple-500
];

export default function ProgressPanel({
  questions,
  answers,
  currentIndex,
  timeRemaining,
  onQuestionSelect,
}: Props) {
  const [hoveredCluster, setHoveredCluster] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const answeredCount = questions.filter(q => {
    if (!q) return false;
    const answer = answers[q.id];
    return answer?.value !== undefined && answer?.value !== null && answer?.value !== '';
  }).length;
  const flaggedCount = questions.filter(q => q && answers[q.id]?.flagged).length;
  const progressPercentage = Math.round((answeredCount / questions.length) * 100);

  // Group questions by cluster
  const getGroupKey = (q: DemoQuestion | null): string | null => {
    if (!q) return null;
    // Support multiple possible fields from BE for grouping/part/cluster
    if (q._clusterParentId) return q._clusterParentId;
    if (q.clusterId) return q.clusterId;
    if (q.clusterContext?.clusterId) return q.clusterContext.clusterId;
    // Some BE payloads may use sectionId or partId
    const maybeSectionId = (q as unknown as Record<string, unknown>)['sectionId'];
    if (typeof maybeSectionId === 'string' && maybeSectionId.length > 0) return maybeSectionId;
    const maybePartId = (q as unknown as Record<string, unknown>)['partId'];
    if (typeof maybePartId === 'string' && maybePartId.length > 0) return maybePartId;
    return null;
  };

  // Calculate clusters and create unique color mapping for each cluster
  // This ensures each cluster gets a unique color and no two clusters share the same color
  const { clusters, clusterColorMap } = useMemo(() => {
    const clustersMap = questions.reduce(
      (acc, question, index) => {
        if (!question) return acc;
        const key = getGroupKey(question);
        if (key) {
          if (!acc[key]) acc[key] = [];
          acc[key].push(index);
        }
        return acc;
      },
      {} as Record<string, number[]>
    );

    const uniqueClusterIds = Object.keys(clustersMap);
    const colorMap: Record<string, string> = {};

    // Assign colors sequentially to ensure uniqueness
    // Each cluster gets a unique color from the palette
    // If there are more clusters than colors, colors will cycle but still be unique per cluster
    uniqueClusterIds.forEach((clusterId, index) => {
      colorMap[clusterId] = CLUSTER_COLORS[index % CLUSTER_COLORS.length];
    });

    return { clusters: clustersMap, clusterColorMap: colorMap };
  }, [questions]);

  const getCenterPoint = (index: number) => {
    const row = Math.floor(index / 5);
    const col = index % 5;
    return {
      x: col * 36 + 14, // 28px button + 8px gap, center at 14px
      y: row * 36 + 14,
    };
  };

  const createCurvedPath = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const controlX = from.x + dx * 0.5;
    const controlY = from.y + dy * 0.3;

    return `M ${from.x} ${from.y} Q ${controlX} ${controlY} ${to.x} ${to.y}`;
  };

  const getQuestionStatus = (index: number): 'answered' | 'flagged' | 'unanswered' => {
    const question = questions[index];
    if (!question) return 'unanswered';
    const answer = answers[question.id];

    // Check if actually answered (has non-empty value)
    const hasAnswer = answer?.value !== undefined && answer?.value !== null && answer?.value !== '';

    if (hasAnswer) return 'answered';
    if (answer?.flagged) return 'flagged';
    return 'unanswered';
  };

  const isQuestionAnswered = (index: number) => {
    const question = questions[index];
    if (!question) return false;
    const answer = answers[question.id];
    return answer?.value !== undefined && answer?.value !== null && answer?.value !== '';
  };

  return (
    <div className='w-56 bg-card/80 backdrop-blur-sm border-r border-border overflow-y-auto progress-panel min-h-screen'>
      <div className='p-3 space-y-4'>
        {/* Progress Stats */}
        <Card
          className='p-3 bg-gradient-to-br from-primary/10 to-accent/10 dark:from-primary/20 dark:to-accent/20 backdrop-blur-sm border-primary/30 shadow-xl hover:shadow-lg transition-all duration-300'
          data-tour='sidebar-overview'
        >
          <h2 className='text-xs font-bold uppercase text-muted-foreground mb-2 tracking-wide flex items-center gap-1.5'>
            <CheckCircle className='w-3 h-3 text-primary' />
            TIẾN ĐỘ
          </h2>

          <div className='space-y-2.5'>
            <div>
              <div className='flex items-center justify-between mb-1.5'>
                <span className='text-xs text-muted-foreground'>Hoàn thành</span>
                <span className='text-xs font-semibold text-primary'>
                  {answeredCount}/{questions.length}
                </span>
              </div>
              <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5'>
                <div
                  className='bg-gradient-to-r from-primary to-primary/80 h-1.5 rounded-full transition-all duration-300'
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>

            <div className='flex items-center gap-2 p-2 bg-card/80 backdrop-blur-sm rounded-md border border-border hover:border-primary/30 transition-all duration-300'>
              <Clock className='w-3 h-3 text-primary' />
              <div>
                <div className='text-sm font-bold text-foreground'>{timeRemaining}</div>
                <div className='text-xs text-muted-foreground'>Thời gian còn</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Question Grid */}
        <div>
          <h2 className='text-xs font-bold uppercase text-muted-foreground mb-2 tracking-wide flex items-center gap-1.5'>
            <MapPin className='w-3 h-3 text-primary' />
            CÂU HỎI
          </h2>

          <div className='relative'>
            {/* Question Grid */}
            <div
              ref={gridRef}
              className='grid grid-cols-5 gap-2 relative z-20'
              data-tour='sidebar-nav'
            >
              {questions.map((question, index) => {
                const groupKey = getGroupKey(question);
                const isClusterQuestion = Boolean(groupKey);
                const clusterId = groupKey || undefined;
                const clusterIndices = clusterId ? clusters[clusterId] : [];

                // Get unique color for this cluster from the mapping
                const clusterColor =
                  isClusterQuestion && clusterId
                    ? clusterColorMap[clusterId] || '#3B82F6'
                    : '#3B82F6';

                const isHovered = hoveredCluster === clusterId;

                return (
                  <div
                    key={index}
                    className='relative'
                    onMouseEnter={() => clusterId && setHoveredCluster(clusterId)}
                    onMouseLeave={() => setHoveredCluster(null)}
                    style={undefined}
                  >
                    <QuestionNavButton
                      number={index + 1}
                      status={getQuestionStatus(index)}
                      isFlagged={question ? Boolean(answers[question.id]?.flagged) : false}
                      isActive={index === currentIndex}
                      onClick={() => onQuestionSelect(index)}
                      className={!question ? 'opacity-50 cursor-not-allowed' : undefined}
                      aria-label={
                        !question
                          ? `Câu ${index + 1} đang tải`
                          : isClusterQuestion
                            ? `Câu hỏi ${clusterIndices.map(i => i + 1).join(', ')} liên quan`
                            : `Câu ${index + 1}`
                      }
                    />

                    {/* Enhanced cluster indicator */}
                    {isClusterQuestion && (
                      <div
                        className='absolute -top-1 -right-1 w-2 h-2 rounded-full border border-white z-30 transition-all duration-200'
                        style={{
                          backgroundColor: clusterColor,
                          boxShadow: isHovered
                            ? `0 4px 8px rgba(0,0,0,0.2), 0 0 0 2px ${clusterColor}40`
                            : '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
